import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import {
  SupportedLanguage,
  translations,
  DEFAULT_LANGUAGE,
  getLanguageDirection,
  TranslationKeys,
  SUPPORTED_LANGUAGES,
} from '../locales';

interface LocalizationContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: TranslationKeys;
  dir: 'ltr' | 'rtl';
  isRTL: boolean;
  formatMessage: (key: string, params?: Record<string, string | number>) => string;
}

const LocalizationContext = createContext<LocalizationContextType | null>(null);

interface LocalizationProviderProps {
  children: ReactNode;
  defaultLanguage?: SupportedLanguage;
  onLanguageChange?: (lang: SupportedLanguage) => void;
}

export function LocalizationProvider({
  children,
  defaultLanguage,
  onLanguageChange,
}: LocalizationProviderProps) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (typeof window !== 'undefined') {
      // 1. Check localStorage for explicit user preference
      const stored = localStorage.getItem('preferredLanguage') as SupportedLanguage;
      if (stored && SUPPORTED_LANGUAGES.find((l) => l.code === stored)) {
        return stored;
      }
      // 2. Auto-detect from browser language
      const browserLang = navigator.language || (navigator as any).userLanguage || '';
      // Try exact match first (e.g. "zh-CN", "pt-BR")
      const exactMatch = SUPPORTED_LANGUAGES.find((l) => l.code === browserLang);
      if (exactMatch) return exactMatch.code;
      // Try prefix match (e.g. "es-MX" → "es", "fr-CA" → "fr")
      const prefix = browserLang.split('-')[0];
      const prefixMatch = SUPPORTED_LANGUAGES.find((l) => l.code === prefix || l.code.startsWith(prefix + '-'));
      if (prefixMatch) return prefixMatch.code;
    }
    return defaultLanguage || DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback(
    (lang: SupportedLanguage) => {
      setLanguageState(lang);
      if (typeof window !== 'undefined') {
        localStorage.setItem('preferredLanguage', lang);
        // Update document direction for RTL languages
        document.documentElement.dir = getLanguageDirection(lang);
        document.documentElement.lang = lang;
      }
      onLanguageChange?.(lang);
    },
    [onLanguageChange],
  );

  // Set initial document direction
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.dir = getLanguageDirection(language);
      document.documentElement.lang = language;
    }
  }, [language]);

  const t = translations[language] || translations[DEFAULT_LANGUAGE];
  const dir = getLanguageDirection(language);
  const isRTL = dir === 'rtl';

  // Helper function to format messages with parameters
  const formatMessage = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split('.');
      let value: any = t;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }

      if (typeof value !== 'string') {
        return key; // Return key if translation not found
      }

      if (!params) return value;

      // Replace {param} placeholders
      return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
        return params[paramKey]?.toString() || `{${paramKey}}`;
      });
    },
    [t],
  );

  return (
    <LocalizationContext.Provider value={{ language, setLanguage, t, dir, isRTL, formatMessage }}>
      {children}
    </LocalizationContext.Provider>
  );
}

export function useLocalization(): LocalizationContextType {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}

// Hook for getting just the translation object
export function useTranslation() {
  const { t, formatMessage } = useLocalization();
  return { t, formatMessage };
}

// Hook for language-related utilities
export function useLanguage() {
  const { language, setLanguage, dir, isRTL } = useLocalization();
  return { language, setLanguage, dir, isRTL, supportedLanguages: SUPPORTED_LANGUAGES };
}
